import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import ProviderLogo from "./ProviderLogo";

/**
 * Radial "power grid" visualization: central hub = RateCurrent, providers
 * positioned around the hub with animated transmission-line pulses.
 *
 * Design Specification Section 3.1/3.5: uses --rc-* tokens only (no hardcoded
 * hex), --rc-green-600 restricted to decorative-graphics use (never text),
 * and provider nodes render through ProviderLogo's neutral initials tile
 * rather than admin-entered `provider.color`, which is unvalidated and can
 * fail contrast (the same reason the old colored-tile pattern was retired
 * elsewhere on the site).
 *
 * Purely decorative: the SVG/graphic is aria-hidden and the same provider
 * list is present as real links in the DOM either way, so nothing here is
 * screen-reader-only content. Motion is suppressed under
 * prefers-reduced-motion (Design Specification Section 3.4), which the
 * global CSS reduced-motion rule can't reach because these are SMIL
 * animations, not CSS transitions.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);
  return reduced;
}

export default function ProviderGrid({ providers = [] }) {
  const reducedMotion = usePrefersReducedMotion();
  if (providers.length === 0) return null;
  const n = providers.length;
  const size = 680;
  const cx = size / 2;
  const cy = size / 2;
  const r = 240;

  const positions = providers.map((p, i) => {
    const angle = (-90 + (360 / n) * i) * (Math.PI / 180);
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    return { ...p, x, y, xPct: (x / size) * 100, yPct: (y / size) * 100, idx: i };
  });

  return (
    <div className="relative" data-testid="provider-grid">
      {/* DESKTOP: radial grid */}
      <div className="hidden md:block relative mx-auto w-full" style={{ maxWidth: size, aspectRatio: "1 / 1" }}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`${n} energy providers arranged around the RateCurrent hub`}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--rc-green-600)" stopOpacity="0.35" />
              <stop offset="70%" stopColor="var(--rc-green-600)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--rc-border)" strokeWidth="1" strokeDasharray="2 6" />
          <circle cx={cx} cy={cy} r={r * 0.65} fill="none" stroke="var(--rc-surface-alt)" strokeWidth="1" strokeDasharray="2 6" />
          <circle cx={cx} cy={cy} r={r * 0.5} fill="url(#hub-glow)" />

          {positions.map((p, i) => (
            <g key={`line-${p.id}`}>
              <line x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="var(--rc-slate-300)" strokeWidth="1.5" />
              {!reducedMotion && (
                <circle r="4" fill="var(--rc-green-600)">
                  <animateMotion
                    dur={`${2.2 + (i % 4) * 0.3}s`}
                    repeatCount="indefinite"
                    path={`M ${cx} ${cy} L ${p.x} ${p.y}`}
                    begin={`${i * 0.25}s`}
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    dur={`${2.2 + (i % 4) * 0.3}s`}
                    repeatCount="indefinite"
                    begin={`${i * 0.25}s`}
                  />
                </circle>
              )}
            </g>
          ))}
        </svg>

        <div
          className="absolute flex flex-col items-center justify-center rounded-full shadow-[0_0_0_8px_rgba(10,37,64,0.06),0_20px_60px_rgba(10,37,64,0.25)] z-10"
          style={{ left: "50%", top: "50%", width: 140, height: 140, transform: "translate(-50%, -50%)", background: "var(--rc-ink)" }}
          data-testid="grid-hub"
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 shadow-lg shadow-emerald-500/40"
            style={{ background: "var(--rc-green-600)" }}
            aria-hidden="true"
          >
            <Zap className="w-6 h-6 text-white fill-white" strokeWidth={2.5} />
          </div>
          <span className="text-white font-extrabold text-sm tracking-tight">RateCurrent</span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold mt-0.5" style={{ color: "var(--rc-green-600)" }}>
            The grid
          </span>
        </div>

        {positions.map((p) => (
          <Link
            key={p.id}
            to={`/plans?provider_id=${p.id}`}
            className="group absolute flex flex-col items-center z-10"
            style={{ left: `${p.xPct}%`, top: `${p.yPct}%`, transform: "translate(-50%, -50%)" }}
            data-testid={`grid-node-${p.slug}`}
          >
            <div className="rounded-2xl shadow-lg border-4 border-white transition-transform duration-300 group-hover:scale-110 group-hover:shadow-xl overflow-hidden">
              <ProviderLogo provider={p} size={64} />
            </div>
            <span
              className="mt-2 text-xs font-semibold whitespace-nowrap bg-white px-2 py-0.5 rounded-md shadow-sm border border-slate-100 group-hover:border-[var(--rc-green-600)] transition-colors"
              style={{ color: "var(--rc-ink)" }}
            >
              {p.name}
            </span>
          </Link>
        ))}
      </div>

      {/* MOBILE: stacked list, no radial layout */}
      <div className="md:hidden relative">
        <div className="flex flex-col items-center gap-5 relative">
          <div className="absolute left-1/2 top-20 bottom-20 w-px -translate-x-1/2" style={{ background: "var(--rc-border)" }} aria-hidden="true" />
          <div
            className="relative z-10 w-28 h-28 rounded-full flex flex-col items-center justify-center shadow-xl"
            style={{ background: "var(--rc-ink)" }}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-1" style={{ background: "var(--rc-green-600)" }} aria-hidden="true">
              <Zap className="w-5 h-5 text-white fill-white" strokeWidth={2.5} />
            </div>
            <span className="text-white font-bold text-xs">RateCurrent</span>
          </div>
          <div className="grid grid-cols-2 gap-4 w-full relative z-10">
            {providers.map((p) => (
              <Link
                key={p.id}
                to={`/plans?provider_id=${p.id}`}
                className="bg-white rounded-xl border border-slate-100 p-3 flex items-center gap-2 shadow-sm"
                data-testid={`grid-node-mobile-${p.slug}`}
              >
                <ProviderLogo provider={p} size={36} />
                <span className="text-xs font-semibold truncate" style={{ color: "var(--rc-ink)" }}>{p.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
